// Role constants and utility functions for role-based access control

export const ROLES = {
  // General roles
  USER: "ROLE_USER",
  ADMIN: "ROLE_ADMIN",
  ORG_ADMIN: "ROLE_ORG_ADMIN",

  // Claim roles
  CLAIM_USER: "ROLE_CLAIM_USER",
  CLAIM_ADMIN: "ROLE_CLAIM_ADMIN",

  // Dashboard roles
  DASHBOARD_USER: "ROLE_DASHBOARD_USER",
  DASHBOARD_ADMIN: "ROLE_DASHBOARD_ADMIN",

  // Custom Reporting roles
  CUSTOM_REPORTING_USER: "ROLE_CUSTOM_REPORTING_USER",
  CUSTOM_REPORTING_ADMIN: "ROLE_CUSTOM_REPORTING_ADMIN",

  // TrueRate Generator roles
  TRUERATE_GENERATOR_USER: "ROLE_TRUERATE_GENERATOR_USER",
  TRUERATE_GENERATOR_ADMIN: "ROLE_TRUERATE_GENERATOR_ADMIN",

  // TruePay Auditor roles
  TRUEPAY_AUDITOR_USER: "ROLE_TRUEPAY_AUDITOR_USER",
  TRUEPAY_AUDITOR_ADMIN: "ROLE_TRUEPAY_AUDITOR_ADMIN",

  // Call Pilot roles
  CALL_PILOT_USER: "ROLE_CALL_PILOT_USER",
  CALL_PILOT_ADMIN: "ROLE_CALL_PILOT_ADMIN",

  // Prior Authorization roles
  PRIOR_AUTH_USER: "ROLE_PRIOR_AUTH_USER",
  PRIOR_AUTH_ADMIN: "ROLE_PRIOR_AUTH_ADMIN",

  // AI Receptionist roles
  AI_RECEPTIONIST_USER: "ROLE_AI_RECEPTIONIST_USER",
  AI_RECEPTIONIST_ADMIN: "ROLE_AI_RECEPTIONIST_ADMIN",

  // Retina Analytics roles
  RETINA_ANALYTICS_USER: "ROLE_RETINA_ANALYTICS_USER",
  RETINA_ANALYTICS_ADMIN: "ROLE_RETINA_ANALYTICS_ADMIN",

  // Premium Analytics roles
  PREMIUM_ANALYTICS_USER: "ROLE_PREMIUM_ANALYTICS_USER",
  PREMIUM_ANALYTICS_ADMIN: "ROLE_PREMIUM_ANALYTICS_ADMIN",

  // AR Management roles
  AR_MANAGEMENT_USER: "ROLE_AR_MANAGEMENT_USER",
  AR_MANAGEMENT_ADMIN: "ROLE_AR_MANAGEMENT_ADMIN",

  // Custom Reporting widget roles
  CUSTOM_REPORTING_WIDGET_TIME_SERIES: "ROLE_CUSTOM_REPORTING_WIDGET_TIME_SERIES",
  CUSTOM_REPORTING_WIDGET_DENIAL_RATE: "ROLE_CUSTOM_REPORTING_WIDGET_DENIAL_RATE",
  CUSTOM_REPORTING_WIDGET_PATIENT_FILTER: "ROLE_CUSTOM_REPORTING_WIDGET_PATIENT_FILTER",
  CUSTOM_REPORTING_WIDGET_TRENDING_AR: "ROLE_CUSTOM_REPORTING_WIDGET_TRENDING_AR",

  // Dashboard widget roles
  DASHBOARD_WIDGET_FINANCIAL_HEALTH: "ROLE_DASHBOARD_WIDGET_FINANCIAL_HEALTH",
  DASHBOARD_WIDGET_CHARGES_PAYMENTS: "ROLE_DASHBOARD_WIDGET_CHARGES_PAYMENTS",
  DASHBOARD_WIDGET_CURRENT_AR: "ROLE_DASHBOARD_WIDGET_CURRENT_AR",
  DASHBOARD_WIDGET_PATIENT_AR: "ROLE_DASHBOARD_WIDGET_PATIENT_AR",
  DASHBOARD_WIDGET_CLAIM_EFFICIENCY: "ROLE_DASHBOARD_WIDGET_CLAIM_EFFICIENCY",
  DASHBOARD_WIDGET_INSURANCE_AR: "ROLE_DASHBOARD_WIDGET_INSURANCE_AR",
  DASHBOARD_WIDGET_JCODE_AR: "ROLE_DASHBOARD_WIDGET_JCODE_AR",

  // AR Management widget roles
  AR_MANAGEMENT_WIDGET_PATIENT_MONITORING: "ROLE_AR_MANAGEMENT_WIDGET_PATIENT_MONITORING",

  // Settings tab roles
  SETTINGS_PRACTICE: "ROLE_SETTINGS_PRACTICE",
  SETTINGS_USERS: "ROLE_SETTINGS_USERS",
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

// Role definitions with labels and descriptions
export const ROLE_DEFINITIONS = [
  // General roles
  {
    value: ROLES.ORG_ADMIN,
    label: "Organization Admin",
    description: "Manage organization settings and users",
    category: "General",
  },

  // Claim roles
  {
    value: ROLES.CLAIM_USER,
    label: "Claim User",
    description: "View and manage claims",
    category: "Claim",
  },
  {
    value: ROLES.CLAIM_ADMIN,
    label: "Claim Admin",
    description: "Full access to claim management",
    category: "Claim",
  },

  // Dashboard roles
  {
    value: ROLES.DASHBOARD_USER,
    label: "Dashboard User",
    description: "View dashboards",
    category: "Dashboard",
  },
  {
    value: ROLES.DASHBOARD_ADMIN,
    label: "Dashboard Admin",
    description: "Manage and configure dashboards",
    category: "Dashboard",
  },

  // Custom Reporting roles
  {
    value: ROLES.CUSTOM_REPORTING_USER,
    label: "Custom Reporting User",
    description: "View custom reports",
    category: "Reporting",
  },
  {
    value: ROLES.CUSTOM_REPORTING_ADMIN,
    label: "Custom Reporting Admin",
    description: "Create and manage custom reports",
    category: "Reporting",
  },

  // TrueRate Generator roles
  {
    value: ROLES.TRUERATE_GENERATOR_USER,
    label: "TrueRate Generator User",
    description: "Use TrueRate Generator",
    category: "Reporting",
  },
  {
    value: ROLES.TRUERATE_GENERATOR_ADMIN,
    label: "TrueRate Generator Admin",
    description: "Full access to TrueRate Generator",
    category: "Reporting",
  },

  // TruePay Auditor roles
  {
    value: ROLES.TRUEPAY_AUDITOR_USER,
    label: "TruePay Auditor User",
    description: "Use TruePay Auditor",
    category: "Reporting",
  },
  {
    value: ROLES.TRUEPAY_AUDITOR_ADMIN,
    label: "TruePay Auditor Admin",
    description: "Full access to TruePay Auditor",
    category: "Reporting",
  },

  // Call Pilot roles
  {
    value: ROLES.CALL_PILOT_USER,
    label: "Call Pilot User",
    description: "Use Call Pilot features",
    category: "Features",
  },
  {
    value: ROLES.CALL_PILOT_ADMIN,
    label: "Call Pilot Admin",
    description: "Manage Call Pilot settings",
    category: "Features",
  },

  // Prior Authorization roles
  {
    value: ROLES.PRIOR_AUTH_USER,
    label: "Prior Authorization User",
    description: "Submit prior authorization requests",
    category: "Features",
  },
  {
    value: ROLES.PRIOR_AUTH_ADMIN,
    label: "Prior Authorization Admin",
    description: "Manage prior authorization workflows",
    category: "Features",
  },

  // AI Receptionist roles
  {
    value: ROLES.AI_RECEPTIONIST_USER,
    label: "AI Receptionist User",
    description: "Use AI Receptionist features",
    category: "Features",
  },
  {
    value: ROLES.AI_RECEPTIONIST_ADMIN,
    label: "AI Receptionist Admin",
    description: "Configure AI Receptionist",
    category: "Features",
  },

  // Retina Analytics roles
  {
    value: ROLES.RETINA_ANALYTICS_USER,
    label: "Retina Analytics User",
    description: "View retina analytics",
    category: "Analytics",
  },
  {
    value: ROLES.RETINA_ANALYTICS_ADMIN,
    label: "Retina Analytics Admin",
    description: "Manage retina analytics",
    category: "Analytics",
  },

  // Premium Analytics roles
  {
    value: ROLES.PREMIUM_ANALYTICS_USER,
    label: "Premium Analytics User",
    description: "View premium analytics",
    category: "Analytics",
  },
  {
    value: ROLES.PREMIUM_ANALYTICS_ADMIN,
    label: "Premium Analytics Admin",
    description: "Manage premium analytics",
    category: "Analytics",
  },

  // AR Management page roles
  {
    value: ROLES.AR_MANAGEMENT_USER,
    label: "AR Management User",
    description: "View AR management page",
    category: "AR Management",
  },
  {
    value: ROLES.AR_MANAGEMENT_ADMIN,
    label: "AR Management Admin",
    description: "Full access to AR management",
    category: "AR Management",
  },

  // Custom Reporting widget roles
  {
    value: ROLES.CUSTOM_REPORTING_WIDGET_TIME_SERIES,
    label: "Widget: Time Series",
    description: "Access to Time Series chart on Custom Reporting",
    category: "Widget Roles",
  },
  {
    value: ROLES.CUSTOM_REPORTING_WIDGET_DENIAL_RATE,
    label: "Widget: Denial Rate",
    description: "Access to Denial Rate widget on Custom Reporting",
    category: "Widget Roles",
  },
  {
    value: ROLES.CUSTOM_REPORTING_WIDGET_PATIENT_FILTER,
    label: "Widget: Patient Filter",
    description: "Access to Patient Filter widget on Custom Reporting",
    category: "Widget Roles",
  },
  {
    value: ROLES.CUSTOM_REPORTING_WIDGET_TRENDING_AR,
    label: "Widget: Trending A/R",
    description: "Access to Trending A/R widget on Custom Reporting",
    category: "Widget Roles",
  },

  // Dashboard widget roles
  {
    value: ROLES.DASHBOARD_WIDGET_FINANCIAL_HEALTH,
    label: "Widget: Financial Health",
    description: "Access to Financial Health widget on Dashboard",
    category: "Widget Roles",
  },
  {
    value: ROLES.DASHBOARD_WIDGET_CHARGES_PAYMENTS,
    label: "Widget: Charges & Payments",
    description: "Access to Charges & Payments widget on Dashboard",
    category: "Widget Roles",
  },
  {
    value: ROLES.DASHBOARD_WIDGET_CURRENT_AR,
    label: "Widget: Current A/R",
    description: "Access to Current A/R widget on Dashboard",
    category: "Widget Roles",
  },
  {
    value: ROLES.DASHBOARD_WIDGET_PATIENT_AR,
    label: "Widget: Patient A/R",
    description: "Access to Patient A/R widget on Dashboard",
    category: "Widget Roles",
  },
  {
    value: ROLES.DASHBOARD_WIDGET_CLAIM_EFFICIENCY,
    label: "Widget: Claim Efficiency",
    description: "Access to Claim Efficiency widget on Dashboard",
    category: "Widget Roles",
  },
  {
    value: ROLES.DASHBOARD_WIDGET_INSURANCE_AR,
    label: "Widget: Insurance A/R",
    description: "Access to Insurance A/R widget on Dashboard",
    category: "Widget Roles",
  },
  {
    value: ROLES.DASHBOARD_WIDGET_JCODE_AR,
    label: "Widget: J-Code A/R",
    description: "Access to J-Code A/R widget on Dashboard",
    category: "Widget Roles",
  },

  // AR Management widget roles
  {
    value: ROLES.AR_MANAGEMENT_WIDGET_PATIENT_MONITORING,
    label: "Widget: Patient Monitoring",
    description: "Access to Patient Monitoring widget on AR Management",
    category: "Widget Roles",
  },

  // Settings tab roles
  {
    value: ROLES.SETTINGS_PRACTICE,
    label: "Settings: Practice Info",
    description: "Access to Practice Info tab in Settings",
    category: "Settings",
  },
  {
    value: ROLES.SETTINGS_USERS,
    label: "Settings: Manage Users",
    description: "Access to Manage Users tab in Settings",
    category: "Settings",
  },
];

// Feature-to-role mapping (users need at least one of these roles)
export const FEATURE_ROLES = {
  // Dashboard Features
  DASHBOARD: [
    ROLES.DASHBOARD_USER,
    ROLES.DASHBOARD_ADMIN,
    ROLES.DASHBOARD_WIDGET_FINANCIAL_HEALTH,
    ROLES.DASHBOARD_WIDGET_CHARGES_PAYMENTS,
    ROLES.DASHBOARD_WIDGET_CURRENT_AR,
    ROLES.DASHBOARD_WIDGET_PATIENT_AR,
    ROLES.DASHBOARD_WIDGET_CLAIM_EFFICIENCY,
    ROLES.DASHBOARD_WIDGET_INSURANCE_AR,
    ROLES.DASHBOARD_WIDGET_JCODE_AR,
    ROLES.ADMIN,
  ],
  CUSTOM_REPORTING: [
    ROLES.CUSTOM_REPORTING_USER,
    ROLES.CUSTOM_REPORTING_ADMIN,
    ROLES.CUSTOM_REPORTING_WIDGET_TIME_SERIES,
    ROLES.CUSTOM_REPORTING_WIDGET_DENIAL_RATE,
    ROLES.CUSTOM_REPORTING_WIDGET_PATIENT_FILTER,
    ROLES.CUSTOM_REPORTING_WIDGET_TRENDING_AR,
    ROLES.ADMIN,
  ],
  TRUERATE_GENERATOR: [ROLES.TRUERATE_GENERATOR_USER, ROLES.TRUERATE_GENERATOR_ADMIN, ROLES.ADMIN],
  TRUEPAY_AUDITOR: [ROLES.TRUEPAY_AUDITOR_USER, ROLES.TRUEPAY_AUDITOR_ADMIN, ROLES.ADMIN],

  // Claim Features
  CLAIM_FLOW: [ROLES.CLAIM_USER, ROLES.CLAIM_ADMIN, ROLES.ADMIN],

  // Admin Features
  USER_MANAGEMENT: [ROLES.ORG_ADMIN, ROLES.ADMIN],
  ORGANIZATION_SETTINGS: [ROLES.ORG_ADMIN, ROLES.ADMIN],

  // Other Features
  CALL_PILOT: [ROLES.CALL_PILOT_USER, ROLES.CALL_PILOT_ADMIN, ROLES.ADMIN],
  PRIOR_AUTH: [ROLES.PRIOR_AUTH_USER, ROLES.PRIOR_AUTH_ADMIN, ROLES.ADMIN],
  AI_RECEPTIONIST: [ROLES.AI_RECEPTIONIST_USER, ROLES.AI_RECEPTIONIST_ADMIN, ROLES.ADMIN],
  RETINA_ANALYTICS: [ROLES.RETINA_ANALYTICS_USER, ROLES.RETINA_ANALYTICS_ADMIN, ROLES.ADMIN],
  PREMIUM_ANALYTICS: [ROLES.PREMIUM_ANALYTICS_USER, ROLES.PREMIUM_ANALYTICS_ADMIN, ROLES.ADMIN],
  AR_MANAGEMENT: [
    ROLES.AR_MANAGEMENT_USER,
    ROLES.AR_MANAGEMENT_ADMIN,
    ROLES.AR_MANAGEMENT_WIDGET_PATIENT_MONITORING,
    ROLES.ADMIN,
  ],

  // Support is always accessible to authenticated users
  SUPPORT: [ROLES.USER, ROLES.ADMIN],
  HOME: [ROLES.USER, ROLES.ADMIN],
} as const;

/**
 * Check if user has any of the required roles for a feature
 */
export const hasAccess = (userRoles: string[], requiredRoles: readonly string[]): boolean => {
  if (!userRoles || userRoles.length === 0) return false;

  // Admin has access to everything
  if (userRoles.includes(ROLES.ADMIN)) return true;

  // Check if user has any of the required roles
  return requiredRoles.some((role) => userRoles.includes(role));
};

/**
 * Check if user has a specific role
 */
export const hasRole = (userRoles: string[], role: string): boolean => {
  if (!userRoles || userRoles.length === 0) return false;
  return userRoles.includes(role);
};

/**
 * Check if user is an admin (either ORG_ADMIN or ADMIN)
 */
export const isAdmin = (userRoles: string[]): boolean => {
  if (!userRoles || userRoles.length === 0) return false;
  return userRoles.includes(ROLES.ORG_ADMIN) || userRoles.includes(ROLES.ADMIN);
};

/**
 * Check if user is a super admin
 */
export const isSuperAdmin = (userRoles: string[]): boolean => {
  if (!userRoles || userRoles.length === 0) return false;
  return userRoles.includes(ROLES.ADMIN);
};

/**
 * Get the corresponding USER role for an ADMIN role
 * e.g., ROLE_DASHBOARD_ADMIN -> ROLE_DASHBOARD_USER
 */
const getCorrespondingUserRole = (adminRole: string): string | null => {
  if (adminRole.endsWith("_ADMIN")) {
    return adminRole.replace("_ADMIN", "_USER");
  }
  return null;
};

/**
 * Get roles that the current user can assign to others.
 * A user can only delegate roles they explicitly hold themselves — even ROLE_ADMIN
 * does not expand the set beyond what the user was actually granted.
 * ROLE_ADMIN and ROLE_USER are system roles and are never assignable.
 * Only _ADMIN variants are shown in the invite modal (USER variants are implicit).
 */
export const getAssignableRoles = (userRoles: string[]): typeof ROLE_DEFINITIONS => {
  if (!userRoles || userRoles.length === 0) return [];

  const assignableRoleValues = new Set<string>();

  userRoles.forEach((role) => {
    assignableRoleValues.add(role);
    const correspondingUserRole = getCorrespondingUserRole(role);
    if (correspondingUserRole) {
      assignableRoleValues.add(correspondingUserRole);
    }
  });

  return ROLE_DEFINITIONS.filter((role) => {
    if (!assignableRoleValues.has(role.value)) return false;
    return role.value.endsWith("_ADMIN");
  });
};

/**
 * Get a friendly display name for a role
 */
export const getRoleLabel = (role: string): string => {
  const definition = ROLE_DEFINITIONS.find((r) => r.value === role);
  return definition?.label || role.replace("ROLE_", "");
};

/**
 * Get all role labels for display
 */
export const getRoleLabels = (roles: string[]): string[] => {
  return roles.map(getRoleLabel);
};
